import { useState } from "react";
import { Loader2, AlertTriangle, Calendar, Filter as FilterIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { adminPurgePreview, adminPurgeBefore } from "@/lib/admin";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTS = [
  { id: "all", label: "Toutes" },
  { id: "active", label: "Actives" },
  { id: "closed", label: "Clôturées" },
];
const LEVEL_OPTS = [
  { id: "all", label: "Toutes" },
  { id: "green", label: "Vert" },
  { id: "orange", label: "Orange" },
  { id: "red", label: "Rouge" },
];

const REQUIRED_PHRASE = "PURGER";

function isoOfDateInputEndOfDay(d) {
  // d is "YYYY-MM-DD" — treat as end-of-day local then convert ISO
  if (!d) return null;
  const [y, m, day] = d.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, day, 23, 59, 59, 999));
  return dt.toISOString();
}

function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  // Format as yyyy-mm-dd in local time
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function PurgeBeforeDialog({ open, onOpenChange, onPurged }) {
  const [date, setDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("closed");
  const [levelFilter, setLevelFilter] = useState("all");
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const beforeISO = isoOfDateInputEndOfDay(date);

  const previewIfPossible = async (override = {}) => {
    const params = {
      before: override.before ?? beforeISO,
      status: override.status ?? statusFilter,
      level: override.level ?? levelFilter,
    };
    if (!params.before) {
      setPreviewCount(null);
      return;
    }
    setPreviewLoading(true);
    try {
      const r = await adminPurgePreview(params);
      setPreviewCount(r.count);
    } catch (_e) {
      setPreviewCount(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!beforeISO) {
      toast.error("Choisissez une date.");
      return;
    }
    if (confirmText.trim() !== REQUIRED_PHRASE) {
      toast.error(`Tapez exactement "${REQUIRED_PHRASE}" pour confirmer.`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminPurgeBefore({
        before: beforeISO,
        status: statusFilter,
        level: levelFilter,
      });
      toast.success(`${r.soft_deleted} alerte${r.soft_deleted > 1 ? "s" : ""} supprimée${r.soft_deleted > 1 ? "s" : ""}.`);
      onPurged?.(r);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDate("");
    setStatusFilter("closed");
    setLevelFilter("all");
    setPreviewCount(null);
    setConfirmText("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        data-testid="purge-dialog"
        className="max-w-[460px] rounded-[20px]"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full text-white"
              style={{ background: "#EF4444" }}
            >
              <AlertTriangle size={18} />
            </span>
            <DialogTitle className="text-[20px] font-bold text-slate-900">
              Purge périodique
            </DialogTitle>
          </div>
          <DialogDescription className="mt-0.5 text-[13px] text-slate-500">
            Supprime (soft delete) toutes les alertes créées avant la date choisie.
            Cette action est rapide mais réversible (Restaurer disponible).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div>
            <label className="sa-label">
              <Calendar size={12} className="mr-1 inline -translate-y-[1px]" />
              Supprimer toutes les alertes avant le…
            </label>
            <input
              type="date"
              data-testid="purge-before-input"
              value={date}
              max={todayISO()}
              onChange={(e) => {
                setDate(e.target.value);
                const newBefore = isoOfDateInputEndOfDay(e.target.value);
                if (newBefore) previewIfPossible({ before: newBefore });
                else setPreviewCount(null);
              }}
              className="sa-input"
            />
          </div>

          <div>
            <label className="sa-label">
              <FilterIcon size={12} className="mr-1 inline -translate-y-[1px]" />
              Statut
            </label>
            <Pills
              value={statusFilter}
              options={STATUS_OPTS}
              onChange={(v) => {
                setStatusFilter(v);
                previewIfPossible({ status: v });
              }}
              testId="purge-filter-status"
            />
          </div>

          <div>
            <label className="sa-label">Niveau</label>
            <Pills
              value={levelFilter}
              options={LEVEL_OPTS}
              onChange={(v) => {
                setLevelFilter(v);
                previewIfPossible({ level: v });
              }}
              testId="purge-filter-level"
            />
          </div>

          {beforeISO ? (
            <div
              data-testid="purge-preview"
              className="rounded-[12px] border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800"
            >
              {previewLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" />
                  Calcul du nombre d'alertes concernées…
                </span>
              ) : previewCount === null ? (
                "Saisissez une date pour estimer le volume."
              ) : (
                <span>
                  <strong>{previewCount}</strong> alerte
                  {previewCount > 1 ? "s" : ""} concernée
                  {previewCount > 1 ? "s" : ""}.
                </span>
              )}
            </div>
          ) : null}

          <div>
            <label className="sa-label">
              Pour confirmer, tapez <strong className="text-red-600">PURGER</strong> :
            </label>
            <input
              type="text"
              data-testid="purge-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="sa-input font-mono uppercase"
              placeholder="PURGER"
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter className="mt-1 flex gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="purge-cancel"
            className="flex-1 rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-[14px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              submitting || !date || confirmText.trim() !== REQUIRED_PHRASE
            }
            data-testid="purge-submit"
            className="flex-1 rounded-[14px] px-5 py-3 text-[14px] font-bold text-white transition disabled:opacity-50"
            style={{
              background: "#EF4444",
              boxShadow: "0 4px 18px -2px rgba(239,68,68,0.55)",
            }}
          >
            {submitting ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Purge…
              </span>
            ) : (
              "Purger"
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Pills({ value, options, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          data-testid={`${testId}-${o.id}`}
          className={`rounded-full px-3 py-1 text-[12px] font-semibold transition ${
            value === o.id
              ? "bg-[var(--sa-blue)] text-white"
              : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
